import { Box, Flex, Grid } from '@chakra-ui/react';
import { Avatar, Loader, Text } from '@kula-ai/components';
import { ArrowSquareUpRight, Mail01, Phone01 } from '@kula-ai/icons-core';
import { FaGithub, FaLinkedin, FaXTwitter } from 'react-icons/fa6';
import { ExperienceView } from '@/components/contacts/details/ExperienceView';
import { EducationView } from '@/components/contacts/details/EducationView';
import { useCandidateProfileQuery } from '../../../api/candidates';
import { useHostContactQuery } from '../../../api/hostContact';
import { buildProfileView } from './profileView';
import { ResumeSection } from './ResumeSection';
import { fromResumePayloads } from './toResumeAttachments';
import type { CandidateProfileCardProps } from './CandidateProfileCard.types';

const Dot = () => (
  <Box as="span" color="secondary-300" px="2px">
    •
  </Box>
);

const Section = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box>
    <Text variant="h6">{label}</Text>
    <Box mt="10px">{children}</Box>
  </Box>
);

export const CandidateProfileCard = ({
  candidate,
  active,
  resumes,
}: CandidateProfileCardProps) => {
  const query = useHostContactQuery(candidate.id, active);
  const contact = query.data ?? null;
  const view = buildProfileView(candidate, contact);
  const loadingDetails = active && query.isLoading && !query.data;

  // Only fetch resumes when the caller didn't already hand us some.
  const resumeQuery = useCandidateProfileQuery(
    candidate.id,
    active && resumes === undefined
  );
  const resumeList =
    resumes ?? fromResumePayloads(resumeQuery.data?.resumes ?? []);

  const experiences = Array.isArray(contact?.experiences)
    ? contact!.experiences
    : [];
  const educations = Array.isArray(contact?.educations)
    ? contact!.educations
    : [];

  return (
    <Box
      data-test-id={`agentforge-candidate-profile-${candidate.id}`}
      bg="base-white"
      border="1px solid"
      borderColor="border-color"
      borderRadius="12px"
      boxShadow="0px 8px 24px rgba(38, 34, 68, 0.06)"
      px="20px"
      py="18px"
      // Stable height so it doesn't collapse to the spinner then jump tall.
      minH="480px"
    >
      <Flex gap="12px" align="flex-start">
        <Avatar
          src={view.avatarUrl}
          name={view.name}
          shape="square"
          size={48}
        />
        <Box flex="1" minW="0">
          <Flex align="center" gap="8px" wrap="wrap">
            <Text variant="h2" as="span">
              {view.name}
            </Text>
            {view.linkedInUrl && (
              <Box
                as="a"
                href={view.linkedInUrl}
                target="_blank"
                rel="noopener noreferrer"
                color="#0A66C2"
                display="inline-flex"
                aria-label="LinkedIn profile"
              >
                <FaLinkedin size={15} />
              </Box>
            )}
            {view.twitterUrl && (
              <Box
                as="a"
                href={view.twitterUrl}
                target="_blank"
                rel="noopener noreferrer"
                color="secondary-900"
                display="inline-flex"
                aria-label="X profile"
              >
                <FaXTwitter size={13} />
              </Box>
            )}
            {view.githubUrl && (
              <Box
                as="a"
                href={view.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                color="secondary-900"
                display="inline-flex"
                aria-label="GitHub profile"
              >
                <FaGithub size={15} />
              </Box>
            )}
            <Box
              as="a"
              href={view.externalHref}
              target="_blank"
              rel="noopener noreferrer"
              color="secondary-400"
              display="inline-flex"
              aria-label="Open full profile"
              _hover={{ color: 'primary-500' }}
            >
              <ArrowSquareUpRight size={15} />
            </Box>
          </Flex>
          {view.headline && (
            <Box mt="2px">
              <Text variant="p" as="span" className="text-secondary-500">
                {view.headline}
              </Text>
            </Box>
          )}
        </Box>
      </Flex>

      {/* Spinner while the contact loads — avoids a sparse → full jump. */}
      {loadingDetails ? (
        <Flex align="center" justify="center" minH="380px">
          <Loader size={16} />
        </Flex>
      ) : (
        <>
          <Flex align="center" wrap="wrap" gap="4px" mt="12px">
            {view.phone && (
              <Flex align="center" gap="4px">
                <Box as="span" color="secondary-400" display="inline-flex">
                  <Phone01 size={14} />
                </Box>
                <Text variant="p" as="span" className="text-secondary-700">
                  {view.phone}
                </Text>
              </Flex>
            )}
            {view.phone && view.email && <Dot />}
            {view.email && (
              <Flex align="center" gap="4px">
                <Box as="span" color="secondary-400" display="inline-flex">
                  <Mail01 size={14} />
                </Box>
                <Text variant="p" as="span" className="text-secondary-700">
                  {view.email}
                </Text>
              </Flex>
            )}
            {view.source && (
              <>
                <Dot />
                <Text variant="p" as="span" className="text-secondary-500">
                  Source: {view.source}
                </Text>
              </>
            )}
          </Flex>
          {view.creditedTo && (
            <Box mt="16px">
              <Section label="Credited to">
                <Flex align="center" gap="8px">
                  <Avatar
                    src={view.creditedToAvatar}
                    name={view.creditedTo}
                    shape="circle"
                    size={22}
                  />
                  <Text variant="p" as="span" className="text-secondary-900">
                    {view.creditedTo}
                  </Text>
                </Flex>
              </Section>
            </Box>
          )}
          {experiences.length > 0 && (
            <Box mt="16px">
              <Section label="Experience">
                <Grid templateColumns="repeat(3, 1fr)" gap="10px">
                  {experiences.slice(0, 6).map((e, i) => (
                    <ExperienceView key={i} experience={e} />
                  ))}
                </Grid>
              </Section>
            </Box>
          )}
          {educations.length > 0 && (
            <Box mt="16px">
              <Section label="Education">
                <Grid templateColumns="repeat(2, 1fr)" gap="10px">
                  {educations.slice(0, 4).map((ed, i) => (
                    <EducationView key={i} education={ed} />
                  ))}
                </Grid>
              </Section>
            </Box>
          )}
          {view.skills.length > 0 && (
            <Box mt="16px">
              <Section label="Skills">
                <Flex wrap="wrap" gap="6px">
                  {view.skills.slice(0, 24).map((s) => (
                    <Flex
                      key={s}
                      align="center"
                      bg="secondary-50"
                      borderRadius="6px"
                      px="8px"
                      py="3px"
                    >
                      <Text
                        variant="p"
                        as="span"
                        className="text-secondary-700"
                      >
                        {s}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </Section>
            </Box>
          )}
          {resumeList.length > 0 && (
            <Box mt="16px">
              <Section label="Resume">
                <ResumeSection resumes={resumeList} />
              </Section>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
